import {
	cn,
	IconButton,
	Input,
	MagnifyingGlassIcon,
	XIcon,
} from "@au-schein-de/au-ui-library";
import type { ReactNode } from "react";
import { PharmacyPickerFilterCards } from "~/features/products/pharmacy-picker/pharmacy-picker-filter-cards.component";
import { PharmacyPickerNoResultsMessage } from "~/features/products/pharmacy-picker/pharmacy-picker-no-results-message.component";
import type { PharmacyPickerSortContext } from "~/features/products/pharmacy-picker/pharmacy-picker-sort-preference";
import { PharmacyWarningBanner } from "~/features/products/pharmacy-picker/pharmacy-warning-banner.component";

export type PharmacyPickerStickyHeaderProps = {
	isElevated: boolean;
	showSearch?: boolean;
	showFilterCards?: boolean;
	searchValue: string;
	searchPlaceholder: string;
	clearSearchLabel: string;
	onSearchChange: (value: string) => void;
	onSearchClear: () => void;
	onMobileSearchOpen: () => void;
	noSearchResults?: boolean;
	topSelected?: boolean;
	paySelected?: boolean;
	onTopChange?: (selected: boolean) => void;
	onPayChange?: (selected: boolean) => void;
	description?: ReactNode;
	embedded?: boolean;
	sortContext: PharmacyPickerSortContext;
	radiusControl?: ReactNode;
	mobileRadiusControl?: ReactNode;
	deliveryMethodControl?: ReactNode;
	expressSelected?: boolean;
	onExpressChange?: (selected: boolean) => void;
	/**
	 * Rendered as a warning banner above the list. The list is empty in this
	 * case, and "no express pharmacies nearby" would be the wrong reading.
	 */
	expressCheckFailedMessage?: ReactNode;
};

export function PharmacyPickerStickyHeader({
	isElevated,
	showSearch = true,
	showFilterCards = false,
	searchValue,
	searchPlaceholder,
	clearSearchLabel,
	onSearchChange,
	onSearchClear,
	onMobileSearchOpen,
	noSearchResults = false,
	topSelected = false,
	paySelected = false,
	onTopChange,
	onPayChange,
	description,
	embedded = false,
	sortContext,
	radiusControl,
	mobileRadiusControl,
	deliveryMethodControl,
	expressSelected = false,
	onExpressChange,
	expressCheckFailedMessage,
}: PharmacyPickerStickyHeaderProps) {
	return (
		<div
			className={cn(
				"relative z-10 shrink-0 bg-white",
				embedded
					? "px-4 pb-4 max-md:px-3 md:px-10"
					: cn(
							"p-4 max-md:rounded-t-none md:rounded-t-2xl transition-shadow",
							isElevated
								? "shadow-[0_4px_20px_0_rgba(33,42,54,0.1)]"
								: "border-b border-gray-200",
						),
			)}
			data-testid="pharmacy-picker-sticky-header"
		>
			<div className={cn("flex flex-col", showFilterCards ? "gap-3" : "")}>
				{showSearch ? (
					<>
						<div className="max-md:hidden">
							<Input
								name="search"
								data-testid="vendor-search"
								leftIcon={
									<MagnifyingGlassIcon size={6} colorToken="gray-700" />
								}
								placeholder={searchPlaceholder}
								value={searchValue}
								onChange={(event) => {
									event.stopPropagation();
									onSearchChange(event.target.value);
								}}
								onKeyDown={(event) => event.stopPropagation()}
								rightIcon={
									searchValue !== "" ? (
										<IconButton
											icon={<XIcon size={6} colorToken="gray-700" />}
											onClick={(event) => {
												event.stopPropagation();
												onSearchClear();
											}}
											aria-label={clearSearchLabel}
											shape="circle"
											size="sm"
											variant="input"
											data-testid="vendor-search-clear"
										/>
									) : undefined
								}
							/>
						</div>
						<button
							type="button"
							className="flex h-12 w-full items-center gap-2 rounded-xl bg-gray-100 px-4 text-left md:hidden"
							onClick={onMobileSearchOpen}
							data-testid="vendor-search-mobile-trigger"
						>
							<MagnifyingGlassIcon size={6} colorToken="gray-700" />
							<span
								className={cn(
									"min-w-0 flex-1 truncate text-body",
									searchValue ? "text-gray-700" : "text-gray-500",
								)}
							>
								{searchValue || searchPlaceholder}
							</span>
						</button>
					</>
				) : null}
				{showFilterCards ? (
					<PharmacyPickerFilterCards
						className={
							embedded ? "max-md:-mx-3 max-md:px-3" : "max-md:-mx-4 max-md:px-4"
						}
						topSelected={topSelected}
						paySelected={paySelected}
						onTopChange={onTopChange}
						onPayChange={onPayChange ?? (() => undefined)}
						sortContext={sortContext}
						radiusControl={radiusControl}
						mobileRadiusControl={mobileRadiusControl}
						deliveryMethodControl={deliveryMethodControl}
						expressSelected={expressSelected}
						onExpressChange={onExpressChange}
					/>
				) : null}
				{expressCheckFailedMessage ? (
					<PharmacyWarningBanner data-testid="pharmacy-picker-express-check-failed">
						{expressCheckFailedMessage}
					</PharmacyWarningBanner>
				) : null}
				{description && !topSelected ? (
					<PharmacyWarningBanner data-testid="pharmacy-picker-why-top-blurb">
						{description}
					</PharmacyWarningBanner>
				) : null}
				{noSearchResults ? (
					<div className="mt-3">
						<PharmacyPickerNoResultsMessage />
					</div>
				) : null}
			</div>
		</div>
	);
}
